import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch,
  Space, Popconfirm, message, Select, Tag, Tooltip
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BookOutlined, TeamOutlined } from '@ant-design/icons';
import { getBranches } from '../../api/branches';
import BranchSelect from '../common/BranchSelect';
import UserSearchSelect from '../common/UserSearchSelect';
import { getClassrooms, createClassroom, updateClassroom, deleteClassroom, getClassroomCourses, syncClassroomCourses } from '../../api/settings';
import { getCourses } from '../../api/courses';
import useDebounce from '../../hooks/useDebounce';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import useServerSort from '../../hooks/useServerSort';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;

const ClassroomsTab = ({ branchId } = {}) => {
  const [classrooms, setClassrooms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState(branchId ?? null);
  const [colorValue, setColorValue] = useState('#1677ff');
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('classrooms', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  // Arşiv dersliğinde ad alanı kilitli. Bilgi backend'den geliyor
  // (`is_archive`) — isim kuralı tek yerde, Classroom modelinde dursun.
  const editingArchive = !!editingClassroom?.is_archive;

  // Kurs bağlama
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseClassroom, setCourseClassroom] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [courseLoading, setCourseLoading] = useState(false);

  const fetchClassrooms = async (page = 1, searchText = debouncedSearch, branch = branchFilter, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const res = await getClassrooms({ page, per_page: perPage, search: searchText, branch_id: branch, sort_by: sortBy || undefined, sort_direction: sortDir || undefined });
      setClassrooms(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch (error) {
      message.error('Derslikler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await getBranches({ per_page: 100 });
      setBranches(res.data.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchClassrooms(1, debouncedSearch, branchFilter);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const data = { ...values, color: colorValue, ...(branchId ? { branch_id: branchId } : {}) };
      if (editingClassroom) {
        await updateClassroom(editingClassroom.id, data);
        message.success('Derslik güncellendi.');
      } else {
        await createClassroom(data);
        message.success('Derslik oluşturuldu.');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingClassroom(null);
      setColorValue('#1677ff');
      fetchClassrooms();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleEdit = (classroom) => {
    setEditingClassroom(classroom);
    setColorValue(classroom.color || '#1677ff');
    form.setFieldsValue({
      ...classroom,
      teacher_ids: (classroom.teachers || []).map(t => t.id),
    });
    setModalOpen(true);
  };

  const handleOpenCourses = async (classroom) => {
    setCourseClassroom(classroom);
    setCourseLoading(true);
    setCourseModalOpen(true);
    try {
      const [coursesRes, linkedRes] = await Promise.all([
        getCourses({ branch_id: classroom.branch_id, per_page: 100, is_active: 1 }),
        getClassroomCourses(classroom.id),
      ]);
      setAllCourses(coursesRes.data?.data || coursesRes.data || []);
      setSelectedCourseIds((linkedRes.data || []).map(c => c.id));
    } catch {
      message.error('Programlar yüklenemedi.');
    } finally {
      setCourseLoading(false);
    }
  };

  const handleSaveCourses = async () => {
    try {
      await syncClassroomCourses(courseClassroom.id, { course_ids: selectedCourseIds });
      message.success('Sertifika programları güncellendi.');
      setCourseModalOpen(false);
    } catch {
      message.error('Güncelleme başarısız.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteClassroom(id);
      message.success('Derslik silindi.');
      fetchClassrooms();
    } catch (error) {
      message.error('Derslik silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Derslik Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : null,
      render: (name, record) => (
        <span>
          {name}
          {record?.is_archive && (
            <Tooltip title="Geçmişten gelen öğrencilerin eski proje ve sertifikaları buraya kaydediliyor. Adı sistemin işleyişine bağlı olduğu için değiştirilemez ve silinemez; takvimde de görünmez.">
              <Tag color="default" style={{ marginLeft: 8 }}>Arşiv</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Renk',
      dataIndex: 'color',
      key: 'color',
      render: (color) => (
        <div style={{ width: 24, height: 24, borderRadius: 4, backgroundColor: color || '#1677ff', border: '1px solid #d9d9d9' }} />
      ),
    },
    {
      title: 'Kapasite',
      dataIndex: 'capacity',
      key: 'capacity',
      sorter: true,
      sortOrder: sortField === 'capacity' ? sortOrder : null,
      render: (v) => `${v} kişi`,
    },
    {
      title: 'Varsayılan Öğretmenler',
      key: 'teachers',
      render: (_, record) => record.teachers?.length > 0
        ? record.teachers.map(t => <Tag key={t.id}>{t.name} {t.surname}</Tag>)
        : <span className="text-gray-400">—</span>,
    },
    {
      title: 'Şube',
      dataIndex: 'branch_id',
      key: 'branch_id',
      render: (branch_id) => {
        const branch = branches.find(b => b.id === branch_id);
        return branch ? branch.name : '—';
      },
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      sortOrder: sortField === 'is_active' ? sortOrder : null,
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateClassroom(record.id, { is_active: checked });
              message.success('Durum güncellendi.');
              fetchClassrooms();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<BookOutlined />} size="small" onClick={() => handleOpenCourses(record)}>
            Programlar
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          {/* Arşiv dersliği silinemez — altındaki geçmiş proje/sertifika
              kayıtları buna bağlı. Arka uç zaten reddediyor; butonu da
              kapatıyoruz ki kullanıcı hataya tıklamasın. */}
          {record.is_archive ? (
            <Tooltip title="Geçmiş dönem kayıtları bu dersliğe bağlı, silinemez.">
              <Button icon={<DeleteOutlined />} danger size="small" disabled>Sil</Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title="Dersliği silmek istediğinize emin misiniz?"
              onConfirm={() => handleDelete(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex gap-3 mb-4">
        <Input
          placeholder="Derslik ara..."
          allowClear
          style={{ width: 250 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!branchId && (
          <Select
            placeholder="Şube filtrele"
            allowClear
            style={{ width: 180 }}
            onChange={(value) => {
              setBranchFilter(value);
              fetchClassrooms(1, debouncedSearch, value);
            }}
          >
            {branches.map((branch) => (
              <Option key={branch.id} value={branch.id}>{branch.name}</Option>
            ))}
          </Select>
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="ml-auto"
          onClick={() => {
            setEditingClassroom(null);
            setColorValue('#1677ff');
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Derslik
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={classrooms}
        rowKey="id"
        loading={loading}
        onChange={(paginationInfo, _filters, sorter) => {
          const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
          setPersistedPageSize(paginationInfo.pageSize);
          fetchClassrooms(paginationInfo.current, debouncedSearch, branchFilter, paginationInfo.pageSize, sField, sDir);
        }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['15', '30', '50', '100'],
          showTotal: (total) => `Toplam ${total} derslik`,
        }}
      />

      {/* Sertifika Programları Modal */}
      <Modal
        title={`${courseClassroom?.name} — Sertifika Programları`}
        open={courseModalOpen}
        onCancel={() => setCourseModalOpen(false)}
        onOk={handleSaveCourses}
        okText="Kaydet"
        cancelText="İptal"
        confirmLoading={courseLoading}
      >
        <p className="text-sm text-gray-500 mb-3">
          Bu derslikte sunulacak sertifika programlarını seçin.
        </p>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Program seçiniz..."
          loading={courseLoading}
          value={selectedCourseIds}
          onChange={setSelectedCourseIds}
          options={allCourses.map(c => ({ value: c.id, label: c.name }))}
          optionFilterProp="label"
          showSearch
        />
      </Modal>

      {/* Derslik Oluştur/Düzenle Modal */}
      <Modal
        title={editingClassroom ? 'Derslik Düzenle' : 'Yeni Derslik'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setColorValue('#1677ff');
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Arşiv dersliğinde ad kilitli: adı sistemin işleyişine bağlı
              (geçmiş proje girişi dersliği adıyla buluyor, takvim adıyla
              dışlıyor). Diğer alanlar serbest. */}
          <Form.Item
            name="name"
            label="Derslik Adı"
            rules={[{ required: true }]}
            extra={editingArchive
              ? 'Geçmiş dönem kayıtları bu dersliğin adına bağlı olduğu için ad değiştirilemez.'
              : undefined}
          >
            <Input placeholder="A Dersliği" maxLength={100} disabled={editingArchive} />
          </Form.Item>
          <Form.Item name="capacity" label="Kapasite" rules={[{ required: true }]}>
            <Input type="number" min="1" placeholder="10" />
          </Form.Item>
          <Form.Item label="Renk">
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={colorValue}
                onChange={(e) => setColorValue(e.target.value)}
                style={{ width: 48, height: 36, cursor: 'pointer', border: '1px solid #d9d9d9', borderRadius: 6, padding: 2 }}
              />
              <span className="text-gray-500 text-sm">{colorValue}</span>
            </div>
          </Form.Item>
          <Form.Item name="teacher_ids" label="Varsayılan Öğretmenler">
            <UserSearchSelect
              mode="multiple"
              placeholder="Öğretmen ara..."
              allowClear
              branchId={branchId}
              extraParams={{ role: ['teacher', 'branch_manager', 'super_admin', 'developer_admin'] }}
              initialOptions={editingClassroom?.teachers}
              renderLabel={(t) => `${t.name} ${t.surname}`}
            />
          </Form.Item>
          {!branchId && (
            <Form.Item name="branch_id" label="Şube" rules={[{ required: true }]}>
              <BranchSelect branches={branches} />
            </Form.Item>
          )}
          {editingClassroom && (
            <Form.Item name="is_active" label="Durum" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); setColorValue('#1677ff'); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingClassroom ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ClassroomsTab;
